import { Badge } from '@/components/ui/badge'
import dayjs from 'dayjs'
import { motion, Variants } from 'framer-motion'
import { CalendarClock, Check } from 'lucide-react'
import React, { useMemo } from 'react'
import { Button } from 'vnetwork-ui'
import {
    STAGE_COLORS,
    cardVariants,
    getDaysInfo,
    getStageColor,
    hasValidEndDate,
} from './career-path-constants'
import type { ActiveStageCardProps } from './career-path-types'
import { getTransitionActions } from './career-path-utils'
import NotesList from './NotesList'
import StageCardActions from './StageCardActions'
import TimelineDot from './TimelineDot'

const ActiveStageCard: React.FC<ActiveStageCardProps> = ({
    path,
    index,
    stageInfo,
    isSelf,
    onTransition,
    onExtend,
    onAddNote,
    onEdit,
    t,
}) => {
    const stage = path.career_path_stage
    const color = STAGE_COLORS[stage] || STAGE_COLORS.offboard
    const daysInfo = getDaysInfo(path.career_path_end_date)

    // Get transition actions — "pass" is the primary button, rest go to dropdown
    const allActions = useMemo(() => getTransitionActions(stage, t), [stage, t])
    const passAction = allActions.find((a) => a.key === 'pass')
    const dropdownActions = allActions.filter((a) => a.key !== 'pass')

    // Show extend for intern/probation only
    const showExtend = stage === 'intern' || stage === 'probation'

    return (
        <motion.div
            className="relative pl-10"
            custom={index}
            variants={cardVariants as Variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            layout
        >
            <TimelineDot stage={stage} isLast index={index} />

            <motion.div
                className={`border-l-4 ${color.border} rounded-lg p-4 bg-white shadow-sm transition-shadow`}
                whileHover={{
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
                transition={{ duration: 0.2 }}
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(stage)}`}
                        >
                            {stageInfo?.label || stage}
                        </span>
                        <Badge variant="outline-info" className="text-[10px]">
                            {t('career_path.current_stage')}
                        </Badge>
                        <span className="text-sm text-gray-500">
                            {dayjs(path.career_path_start_date).format(
                                'DD/MM/YYYY'
                            )}
                            {hasValidEndDate(path.career_path_end_date) &&
                                ` → ${dayjs(path.career_path_end_date).format('DD/MM/YYYY')}`}
                        </span>
                        {daysInfo && (
                            <span
                                className={`text-xs font-medium ${daysInfo.isOverdue ? 'text-red-600' : 'text-green-600'}`}
                            >
                                {daysInfo.isOverdue
                                    ? t('career_path.overdue', {
                                          days: daysInfo.days,
                                      })
                                    : t('career_path.days_remaining', {
                                          days: daysInfo.days,
                                      })}
                            </span>
                        )}
                    </div>

                    {!isSelf && (
                        <StageCardActions
                            transitionActions={dropdownActions}
                            onTransition={onTransition}
                            onEdit={onEdit}
                            t={t}
                        />
                    )}
                </div>

                {/* Note */}
                {path.career_path_note && (
                    <p className="mt-2 text-sm text-gray-600">
                        {path.career_path_note}
                    </p>
                )}

                {/* Notes (above action buttons) */}
                <NotesList
                    notes={path.notes || []}
                    isActive
                    isSelf={isSelf}
                    isExpanded
                    onToggleExpand={() => {}}
                    onAddNote={onAddNote}
                    t={t}
                />

                {/* Primary action buttons */}
                {!isSelf && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {passAction && (
                            <Button
                                variant="contained"
                                size="small"
                                className="!bg-green-600 hover:!bg-green-700"
                                onClick={() => onTransition(passAction)}
                            >
                                <Check className="w-4 h-4 mr-1" />
                                {passAction.label}
                            </Button>
                        )}
                        {showExtend && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onExtend}
                            >
                                <CalendarClock className="w-4 h-4 mr-1" />
                                {t('career_path.extend_period')}
                            </Button>
                        )}
                    </div>
                )}
            </motion.div>
        </motion.div>
    )
}

export default ActiveStageCard
